import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MainLayout } from '@/components/layout';
import { LoadingSpinner } from '@/components/common';
import {
  auditApi,
  usersApi,
  type OperationLogEntry,
  type OperationTypeInfo,
  type UserInfo,
} from '@/api';

const getOperationTypeColor = (type: number | null) => {
  switch (type) {
    case 0: // Login
      return 'primary';
    case 1: // View
      return 'info';
    case 2: // Add Category
    case 3: // Add Software
    case 4: // Publish Release
      return 'success';
    case 5: // Download
      return 'secondary';
    default:
      return 'default';
  }
};

export function AuditLogPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Data state
  const [logs, setLogs] = useState<OperationLogEntry[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [operationTypes, setOperationTypes] = useState<OperationTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Filter state
  const [filterMemberId, setFilterMemberId] = useState<string>('');
  const [filterOperationType, setFilterOperationType] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // Fetch operation types and users on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [typesResponse, usersResponse] = await Promise.all([
          auditApi.getOperationTypes(),
          usersApi.getUsers(),
        ]);

        if (typesResponse.success && typesResponse.data) {
          setOperationTypes(typesResponse.data);
        }

        if (usersResponse.success && usersResponse.data) {
          setUsers(usersResponse.data);
        }
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
      }
    };

    fetchMetadata();
  }, []);

  // Fetch logs with filters
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auditApi.getOperationLogs({
        memberId: filterMemberId ? parseInt(filterMemberId, 10) : undefined,
        operationType: filterOperationType ? parseInt(filterOperationType, 10) : undefined,
        startDate: filterStartDate ? new Date(filterStartDate).toISOString() : undefined,
        endDate: filterEndDate ? new Date(filterEndDate).toISOString() : undefined,
        page: page + 1, // API uses 1-based pagination
        pageSize: rowsPerPage,
      });

      if (response.success && response.data) {
        setLogs(response.data.items);
        setTotalCount(response.data.totalCount);
      }
    } catch (err) {
      enqueueSnackbar(t('error.serverError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    filterMemberId,
    filterOperationType,
    filterStartDate,
    filterEndDate,
    page,
    rowsPerPage,
    enqueueSnackbar,
    t,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterMemberId('');
    setFilterOperationType('');
    setFilterStartDate('');
    setFilterEndDate('');
    setPage(0);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <MainLayout maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('audit.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('audit.description')}
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
        >
          <TextField
            select
            size="small"
            label={t('audit.filterUser')}
            value={filterMemberId}
            onChange={(e) => {
              setFilterMemberId(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">{t('audit.allUsers')}</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id.toString()}>
                {user.name || user.email}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t('audit.filterOperation')}
            value={filterOperationType}
            onChange={(e) => {
              setFilterOperationType(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('audit.allOperations')}</MenuItem>
            {operationTypes.map((type) => (
              <MenuItem key={type.code} value={type.code.toString()}>
                {t(`audit.operationType.${type.code}`, { defaultValue: type.name })}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            label={t('audit.startDate')}
            value={filterStartDate}
            onChange={(e) => {
              setFilterStartDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            type="date"
            size="small"
            label={t('audit.endDate')}
            value={filterEndDate}
            onChange={(e) => {
              setFilterEndDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={fetchLogs} title={t('common.search')}>
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={handleClearFilters} title={t('common.reset')}>
              <ClearIcon />
            </IconButton>
          </Box>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('audit.columns.time')}</TableCell>
                    <TableCell>{t('audit.columns.user')}</TableCell>
                    <TableCell>{t('audit.columns.operation')}</TableCell>
                    <TableCell>{t('audit.columns.message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          {t('audit.noLogs')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(log.operationTime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {log.memberName || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.memberEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`audit.operationType.${log.operationType}`, {
                              defaultValue: log.operationTypeName || 'Unknown',
                            })}
                            color={getOperationTypeColor(log.operationType) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {log.message || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage={t('common.rowsPerPage')}
            />
          </>
        )}
      </Paper>
    </MainLayout>
  );
}
